import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import api from '../../service/api';
import { Trash2, Plus, ArrowRight, ArrowLeft, Check } from "lucide-react";
import "../../css/PrescricaoTreinoForca.css";
import "../../css/style.css";
import { formatarNumero } from '../../utils/formato';

function PrescricaoTreinoForca({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
  // ---------------- Estado do Fluxo Simplificado (3 Etapas Fixas) ----------------
  const [etapa, setEtapa] = useState(1);
  const [numTreinos, setNumTreinos] = useState(1);
  const [treinoAtivoIndex, setTreinoAtivoIndex] = useState(0); // Controla qual aba de treino está visível na Etapa 1

  // Dados centrais do treino
  const [treinos, setTreinos] = useState([{ nome: "Série A", descricao: "", exercicios: [], detalhes: {} }]);
  const [diasTreinos, setDiasTreinos] = useState({});
  const [intensidades, setIntensidades] = useState({});
  const [dataInicial, setDataInicial] = useState("");
  const [numSemanas, setNumSemanas] = useState(1); // Quantidade de semanas do ciclo

  // Periodização (Etapa 2 -> influencia Etapa 3)
  const [fazerPeriodizacao, setFazerPeriodizacao] = useState(false);
  // Fonte da carga usada nas zonas de intensidade da Etapa 3: "descrita" | "1rm"
  const [cargaSource, setCargaSource] = useState("descrita");

  // Modais e UI
  const [showRMModal, setShowRMModal] = useState(false);
  const [rmTeste, setRmTeste] = useState({});
  const [isNextModal, setIsNextModal] = useState(false);
  const [grupoSelecionado, setGrupoSelecionado] = useState("Peitoral");
  const [novoExercicioNome, setNovoExercicioNome] = useState("");

  const openNextModal = () => setIsNextModal(true);
  const closeNextModal = () => setIsNextModal(false);

  const DIAS = useMemo(() => [
    { key: "mon", label: "Seg" },
    { key: "tue", label: "Ter" },
    { key: "wed", label: "Qua" },
    { key: "thu", label: "Qui" },
    { key: "fri", label: "Sex" },
    { key: "sat", label: "Sáb" },
    { key: "sun", label: "Dom" },
  ], []);

  // Opções de intensidade (%1RM): 100, 95, ..., 55, 50
  const INTENSIDADE_OPCOES = useMemo(
    () => [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50],
    []
  );

  // Banco de Exercícios Pré-cadastrados (Mantido)
  const [exerciciosPre, setExerciciosPre] = useState({
    Peitoral: ["Supino reto com barra", "Supino reto com halteres", "Supino inclinado com halteres", "Peck deck", "Crossover no cabo"],
    "Dorsais (Costas)": ["Puxada frontal na polia", "Barra fixa", "Remada curvada com barra", "Remada unilateral com halter"],
    "Deltoides (Ombros)": ["Desenvolvimento com halteres", "Elevação lateral com halteres", "Crucifixo inverso"],
    Bíceps: ["Rosca direta com barra", "Rosca alternada", "Rosca martelo"],
    Tríceps: ["Tríceps pulley (barra)", "Tríceps pulley (corda)", "Tríceps testa"],
    "Pernas (ênfase geral)": ["Agachamento livre", "Leg press", "Cadeira extensora", "Agachamento búlgaro"],
    Glúteos: ["Hip thrust", "Cadeira abdutora", "Levantamento terra romeno"],
    Panturrilhas: ["Elevação de panturrilha em pé", "Elevação de panturrilha sentado"],
    Abdômen: ["Abdominal crunch", "Prancha frontal", "Abdominal infra"],
  });

  // Carregar dados existentes do BD
  useEffect(() => {
    if (evaluationData?.prescricaoForca) {
      const p = evaluationData.prescricaoForca;
      if (p.numTreinos) {
        setNumTreinos(p.numTreinos);
        // Garante que o treino ativo não fique fora do índice ao carregar
        setTreinoAtivoIndex(0);
      }
      if (p.treinos) setTreinos(p.treinos);
      if (p.diasTreinos) setDiasTreinos(p.diasTreinos);
      if (p.intensidades) setIntensidades(p.intensidades);
      if (p.dataInicial) setDataInicial(p.dataInicial);
      if (p.numSemanas) setNumSemanas(p.numSemanas);
      else if (p.qtdTreinosTotal) setNumSemanas(p.qtdTreinosTotal);
      if (typeof p.fazerPeriodizacao === "boolean") setFazerPeriodizacao(p.fazerPeriodizacao);
      if (p.cargaSource) setCargaSource(p.cargaSource);
      if (p.exerciciosPre) setExerciciosPre(p.exerciciosPre);
    }
  }, [evaluationData]);

  // Sincronizar quantidade de rotinas com o array de objetos
  useEffect(() => {
    setTreinos((prev) => {
      const next = [...prev];
      while (next.length < numTreinos) {
        const letra = String.fromCharCode(65 + next.length); // A, B, C, D...
        next.push({
          nome: `Série ${letra}`,
          descricao: "",
          exercicios: [],
          detalhes: {},
        });
      }
      return next.slice(0, numTreinos);
    });

    setDiasTreinos((prev) => {
      const next = { ...prev };
      for (let i = 0; i < numTreinos; i++) {
        if (!next[i]) {
          next[i] = DIAS.reduce((acc, d) => ({ ...acc, [d.key]: false }), {});
        }
      }
      return next;
    });

    if (treinoAtivoIndex >= numTreinos) {
      setTreinoAtivoIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numTreinos, DIAS]);

  // Envio para a API
  const handlePrescricaoForcaRegister = async () => {
    const payload = {
      prescricaoForca: {
        numTreinos, treinos, diasTreinos, intensidades,
        dataInicial, numSemanas, fazerPeriodizacao, cargaSource, exerciciosPre
      },
      evaluationId
    };

    try {
      const response = await api.post(`/api/student/${aluno.id}/evaluation/prescricao-forca`, payload, { withCredentials: true });
      if (response.status === 200) {
        if (response.data.evaluationId && setEvaluationId) {
          if (!evaluationId) {
            if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
            aluno.avaliacoesRealizadas.push({ id: response.data.evaluationId, data: new Date().toISOString() });
          }
          setEvaluationId(response.data.evaluationId);
        }
        if (updateEvaluationData) updateEvaluationData({ prescricaoForca: payload.prescricaoForca });
        openNextModal();
      }
    } catch (error) {
      console.error('Erro ao salvar Prescrição Força:', error);
    }
  };

  // Manipuladores de Exercícios e Detalhes (Unificados)
  const addExercicioNoTreino = (exercicio) => {
    setTreinos((prev) => {
      const next = [...prev];
      const t = next[treinoAtivoIndex];
      if (t.exercicios.includes(exercicio)) return prev;

      next[treinoAtivoIndex] = {
        ...t,
        exercicios: [...t.exercicios, exercicio],
        detalhes: {
          ...t.detalhes,
          [exercicio]: { series: "3", reps: "12", carga: "", descanso: "60", rm1: 0 } // Valores padrão ajudam na velocidade
        }
      };
      return next;
    });
  };

  const removeExercicioDoTreino = (exercicio) => {
    setTreinos((prev) => {
      const next = [...prev];
      const t = next[treinoAtivoIndex];
      const detalhes = { ...t.detalhes };
      delete detalhes[exercicio];
      next[treinoAtivoIndex] = {
        ...t,
        exercicios: t.exercicios.filter((e) => e !== exercicio),
        detalhes
      };
      return next;
    });
  };

  const updateDetalhe = (exercicio, field, value) => {
    setTreinos((prev) => {
      const next = [...prev];
      const t = next[treinoAtivoIndex];
      t.detalhes[exercicio] = { ...t.detalhes[exercicio], [field]: value };
      return next;
    });
  };

  const preencherTodosCampos = (field, value) => {
    setTreinos((prev) => {
      const next = [...prev];
      const t = next[treinoAtivoIndex];
      t.exercicios.forEach((ex) => {
        t.detalhes[ex] = { ...t.detalhes[ex], [field]: value };
      });
      return next;
    });
  };

  const addExercicioNoGrupo = () => {
    const nome = novoExercicioNome.trim();
    if (!nome) return;
    if (grupoSelecionado === "Todos") return; // "Todos" é apenas uma visão agregada
    setExerciciosPre(prev => {
      const atual = prev[grupoSelecionado] || [];
      if (atual.some(x => x.toLowerCase() === nome.toLowerCase())) return prev;
      return { ...prev, [grupoSelecionado]: [...atual, nome] };
    });
    setNovoExercicioNome("");
  };

  // Cálculos de Volume e Fila Cíclica (Sessões)
  const calcVolume = (det) => Number(det?.series || 0) * Number(det?.reps || 0) * Number(det?.carga || 0);

  // Gera as sessões percorrendo `numSemanas` semanas a partir da data inicial.
  // Ex.: 3 treinos (Seg/Qua/Sex) x 2 semanas = 6 sessões.
  const sessoes = useMemo(() => {
    if (!dataInicial) return [];

    const [y, m, d] = dataInicial.split("-").map(Number);
    if (!y || !m || !d) return [];

    // getDay(): 0=Dom, 1=Seg ... 6=Sáb
    const getDayKey = (dt) =>
      ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][dt.getDay()];

    const res = [];
    const totalDias = Math.max(0, Number(numSemanas) || 0) * 7;

    for (let offset = 0; offset < totalDias; offset++) {
      const data = new Date(y, m - 1, d + offset); // data local (evita shift de fuso)
      const dayKey = getDayKey(data);
      const week = Math.floor(offset / 7) + 1;

      for (let ti = 0; ti < numTreinos; ti++) {
        if (diasTreinos[ti]?.[dayKey]) {
          res.push({ date: data, treinoIndex: ti, week });
        }
      }
    }
    return res;
  }, [dataInicial, numSemanas, diasTreinos, numTreinos]);

  // ----------------- RENDERIZAÇÃO DAS ETAPAS -----------------

  // ETAPA 1: Configuração Total de Rotinas e Exercícios (Em uma tela única)
  const renderEtapaDefinicaoTreinos = () => {
    const t = treinos[treinoAtivoIndex] || { nome: "", descricao: "", exercicios: [], detalhes: {} };
    const listaGrupo =
      grupoSelecionado === "Todos"
        ? Array.from(new Set(Object.values(exerciciosPre).flat()))
        : (exerciciosPre[grupoSelecionado] || []);

    return (
      <div className="questions-container">
        {/* Lado Esquerdo: Montagem da Rotina Selecionada */}
        <div className="questions-66">
          <div className="question-item">
            <div className="items-center">
              <div className="question-title">Montagem do treinamento de força</div>
              <div className="flex">
                <label className="sub-title-ptf">Quantidade de Rotinas:</label>
                <select
                  className="select-qnt-rotinas"
                  value={numTreinos}
                  onChange={(e) => setNumTreinos(Math.max(1, Math.min(5, Number(e.target.value))))}
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                </select>
              </div>
            </div>

            {/* Abas Superiores para alternar rápido entre Treino A, B, C... */}
            <div className="container-botao-rotinas">
              {treinos.map((treino, idx) => (
                <button
                  key={idx} type="button"
                  onClick={() => setTreinoAtivoIndex(idx)}
                  className={`botao-rotinas ${
                    treinoAtivoIndex === idx
                      ? "botao-rotinas-ativo"
                      : "botao-rotinas-inativo"
                  }`}
                >
                  {treino.nome || `Treino ${idx + 1}`} ({treino.exercicios.length})
                </button>
              ))}
            </div>

            <div className="container-rotinas-e-exercicios">
              {/* Input para renomear a aba atual */}
              <div className="mb-4 flex items-center gap-2">
                <label className="sub-title-ptf">Nome desta rotina:</label>
                <input
                  className="entradas-gerais-rotinas"
                  type="text" placeholder="Ex: Série A - Peito e Tríceps"
                  value={t.nome}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTreinos(prev => { const n = [...prev]; n[treinoAtivoIndex].nome = val; return n; });
                  }}
                />
              </div>

              {/* Descrição da rotina */}
              <div className="mb-4 flex items-center gap-2 pad-top-5">
                <label className="sub-title-ptf">Descrição:</label>
                <input
                  className="entradas-gerais-rotinas"
                  type="text" placeholder="Ex: Foco em hipertrofia, membros superiores..."
                  value={t.descricao || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTreinos(prev => { const n = [...prev]; n[treinoAtivoIndex].descricao = val; return n; });
                  }}
                />
              </div>

              {/* Planilha Direta de Edição dos Exercícios daquela Aba */}
              {t.exercicios.length === 0 ? (
                <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-lg text-slate-400">
                  Selecione os exercícios na lista ao lado para montar o {t.nome}.
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {/* Preenchimento Rápido em Massa */}
                  <div className="pad-top-10">
                    <table className="tabela-exercicios-da-rotina">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600 uppercase text-xs tracking-wider border-b">
                          <th className="p-2">Exercício</th>
                          <th className="p-2 text-center w-16">Séries</th>
                          <th className="p-2 text-center w-16">Reps</th>
                          <th className="p-2 text-center w-16">Carga</th>
                          <th className="p-2 text-center w-20">Intervalo(s)</th>
                          <th className="p-2 text-center">Vol.</th>
                          <th className="p-2 text-center">1RM</th>
                          <th className="p-2 text-center w-10"></th>
                        </tr>
                        <tr className="linha-preencher-toda-coluna">
                          <th className="p-2">Preencher toda coluna</th>
                          <th className="celula-tabela"><input type="number" className="entradas-gerais-rotinas entrada-preencher-coluna" onChange={(e) => preencherTodosCampos("series", e.target.value)} /></th>
                          <th className="celula-tabela"><input type="number" className="entradas-gerais-rotinas" onChange={(e) => preencherTodosCampos("reps", e.target.value)} /></th>
                          <th className="celula-tabela"></th>
                          <th className="celula-tabela"><input type="number" className="entradas-gerais-rotinas" onChange={(e) => preencherTodosCampos("descanso", e.target.value)} /></th>
                          <th className="p-2 text-center"></th>
                          <th className="p-2 text-center"></th>
                          <th className="p-2 text-center w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.exercicios.map((ex) => {
                          const det = t.detalhes?.[ex] || { series: "", reps: "", carga: "", descanso: "", rm1: 0 };
                          return (
                            <tr key={ex} className="border-b hover:bg-slate-50 transition-colors">
                              <td className="p-2 font-medium text-slate-700">{ex}</td>
                              <td className="celula-tabela"><input className="entradas-gerais-rotinas" type="number" value={det.series} onChange={(e) => updateDetalhe(ex, "series", e.target.value)} /></td>
                              <td className="celula-tabela"><input className="entradas-gerais-rotinas" type="number" value={det.reps} onChange={(e) => updateDetalhe(ex, "reps", e.target.value)} /></td>
                              <td className="celula-tabela"><input className="entradas-gerais-rotinas" type="number" placeholder="kg" value={det.carga} onChange={(e) => updateDetalhe(ex, "carga", e.target.value)} /></td>
                              <td className="celula-tabela"><input className="entradas-gerais-rotinas" type="number" value={det.descanso} onChange={(e) => updateDetalhe(ex, "descanso", e.target.value)} /></td>
                              <td className="p-2 text-center font-mono text-xs text-slate-500">{calcVolume(det)}</td>
                              <td className="p-2 text-center font-mono text-xs text-slate-500">{det.rm1 ? formatarNumero(det.rm1, 1) : "-"}</td>
                              <td className="p-1 text-center">
                                <button type="button" className="text-red-500 hover:text-red-700 p-1" onClick={() => removeExercicioDoTreino(ex)}>
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button
                    type="button" className="btt-popup-1rm"
                    onClick={() => {
                      const base = {}; t.exercicios.forEach(ex => { base[ex] = { carga: "", reps: "" }; });
                      setRmTeste(base); setShowRMModal(true);
                    }}
                  >
                    Estimativa de 1RM através do teste de repetições máximas
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Lado Direito: Drawer Rápido de Seleção de Exercícios */}
        <div className="questions-33">
          <div className="question-item bg-white p-5 rounded-lg shadow-sm border border-slate-100 flex flex-col gap-3">
            <div className="question-title">Lista de Exercícios</div>
            <div>
              <span>Selecione o grupo muscular: </span>
              <select
                className="select-qnt-rotinas"
                value={grupoSelecionado} onChange={(e) => setGrupoSelecionado(e.target.value)}
              >
                <option value="Todos">Todos</option>
                {Object.keys(exerciciosPre).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>


            <div className="lista-banco-exercicios">
              {listaGrupo.map((ex) => {
                const jaAdicionado = t.exercicios.includes(ex);
                return (
                  <div key={ex} className="exercicio-do-banco">
                    <button
                      type="button" disabled={jaAdicionado}
                      className={`botao-adicionar-exercicio-banco ${
                        jaAdicionado ? "botao-adicionar-exercicio-banco-selecionado" : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                      }`}
                      onClick={() => addExercicioNoTreino(ex)}
                    >
                      {jaAdicionado ? <Check size={14} /> : <Plus size={14} />}
                    </button>
                    <span className={`flex-1 ${jaAdicionado ? "text-slate-400 line-through" : "text-slate-700 font-medium"}`}>{ex}</span>
                  </div>
                );
              })}
            </div>

            {grupoSelecionado !== "Todos" && (
              <div className="campo-criar-exercicio">
                <input
                  type="text" placeholder="Criar exercício..." className="entradas-gerais-rotinas"
                  value={novoExercicioNome} onChange={(e) => setNovoExercicioNome(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addExercicioNoGrupo()}
                />
                <button type="button" className="bg-slate-800 text-white p-2 rounded-md text-xs font-bold" onClick={addExercicioNoGrupo}>Adicionar</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ETAPA 2: Frequência Semanal, Semanas do ciclo e Periodização
  const renderEtapaCalendario = () => (
    <div className="questions-container font-sans text-slate-800">
      <div className="questions-left">
        <div className="question-item bg-white p-6 rounded-lg shadow-sm border border-slate-100">
          <div className="question-title">Distribuição Semanal de Treino</div>

          <div className="data-inicio-e-quantidade">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Data de Início:</label>
              <input type="date" className="entradas-gerais-rotinas" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
            </div>
            <div className="pad-top-5">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Quantidade de semanas:</label>
              <input
                type="number" min={1} className="entradas-gerais-rotinas entrada-qnt-treinos-campo"
                value={numSemanas}
                onChange={(e) => setNumSemanas(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
          </div>

          <div className="pad-top-10 data-inicio-e-quantidade">Agendamento:</div>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-slate-100 border-b">
                <tr>
                  <th className="p-3 font-semibold text-slate-700">Rotina</th>
                  {DIAS.map((d) => <th key={d.key} className="p-3 text-center font-semibold text-slate-700">{d.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {treinos.map((treino, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-3 font-bold text-slate-800">{treino.nome}</td>
                    {DIAS.map((d) => (
                      <td key={d.key} className="p-3 text-center">
                        <input
                          type="checkbox" className="w-4 h-4 text-blue-600 rounded"
                          checked={!!diasTreinos?.[i]?.[d.key]}
                          onChange={() => setDiasTreinos(prev => ({
                            ...prev,
                            [i]: { ...(prev[i] || {}), [d.key]: !(prev[i]?.[d.key]) }
                          }))}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="questions-right w-80">
        <div className="question-item bg-white p-5 rounded-lg shadow-sm border border-slate-100">
          <div className="question-title">Periodização do Treino de Força</div>

          {/* Deseja fazer a periodização? */}
          <div className="pad-top-5" style={{ marginBottom: 10 }}>
            <label className="sub-title-ptf">Deseja fazer a periodização?</label>
            <label style={{ marginLeft: 12 }}>
              <input
                type="radio" name="fazerPeriodizacao"
                checked={fazerPeriodizacao === true}
                onChange={() => setFazerPeriodizacao(true)}
              />{" "}
              Sim
            </label>
            <label style={{ marginLeft: 12 }}>
              <input
                type="radio" name="fazerPeriodizacao"
                checked={fazerPeriodizacao === false}
                onChange={() => setFazerPeriodizacao(false)}
              />{" "}
              Não
            </label>
          </div>

          {fazerPeriodizacao && (
            <div className="max-h-72 overflow-y-auto divide-y text-xs border rounded-md bg-slate-50">
              {sessoes.length === 0 ? (
                <div className="p-4 text-slate-400 text-center">Defina a data de início, a quantidade de semanas e os dias da semana para gerar a periodização.</div>
              ) : (
                sessoes.map((s, idx) => {
                  const key = `${s.date.toISOString()}-${s.treinoIndex}`;
                  const val = intensidades[key] || {};
                  return (
                    <div key={idx} className="container-data-intensidade">
                      <div className="flex justify-between font-bold text-slate-700">
                        <span>Semana {s.week} —</span>
                        <span className="text-blue-600"> {treinos[s.treinoIndex]?.nome}</span>
                      </div>
                      <div className="container-reps-intensidade">
                        <input
                          type="number" placeholder="Séries" value={val.series || ""}
                          onChange={(e) => setIntensidades(p => ({ ...p, [key]: { ...p[key], series: e.target.value } }))}
                        />
                        <input
                          type="number" placeholder="Repetições" value={val.reps || ""}
                          onChange={(e) => setIntensidades(p => ({ ...p, [key]: { ...p[key], reps: e.target.value } }))}
                        />
                      </div>
                      <div className="container-reps-intensidade pad-top-5">
                        <select
                          className="select-qnt-rotinas"
                          value={val.intensidade || ""}
                          onChange={(e) => setIntensidades(p => ({ ...p, [key]: { ...p[key], intensidade: e.target.value } }))}
                        >
                          <option value="">Intensidade (%1RM)</option>
                          {INTENSIDADE_OPCOES.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <input
                          type="number" placeholder="Intervalo (s)" value={val.intervalo || ""}
                          onChange={(e) => setIntensidades(p => ({ ...p, [key]: { ...p[key], intervalo: e.target.value } }))}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ETAPA 3: Visão Geral / Dashboard Final
  const renderEtapaResultados = () => {
    const percentCols = INTENSIDADE_OPCOES; // 100, 95, ..., 50

    // verifica se existe algum 1RM estimado (calculado na Etapa 1)
    const temAlgum1RM = treinos.some((t) =>
      t?.exercicios?.some((ex) => Number(t.detalhes?.[ex]?.rm1 || 0) > 0)
    );

    const selecionarCarga1RM = () => {
      if (!temAlgum1RM) {
        alert("Você não calculou o 1RM. Realize o teste de 1RM na Etapa 1 antes de usar esta opção.");
      }
      setCargaSource("1rm");
    };

    return (
      <div className="questions-container font-sans text-slate-800">
        <div className="questions-100 flex flex-col gap-6">
          <div className="question-item bg-white p-6 rounded-lg shadow-sm border border-slate-100 resultados-treino-forca">
            <div className="question-title">Periodização do Treino de Força</div>

            {/* Cronograma de Sessões */}
            <div className="mb-6">
              <div className="font-bold text-sm text-slate-700 mb-2">Cronograma Sequencial das Próximas Aulas</div>
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                <table className="standard-table w-full text-left text-sm border-collapse">
                  <thead className="bg-slate-50 sticky top-0 border-b">
                    <tr>
                      <th className="p-2 pl-4 font-semibold">Data</th>
                      <th className="p-2 font-semibold">Ficha de Treino</th>
                      {fazerPeriodizacao && (
                        <>
                          <th className="p-2 text-center font-semibold">Intensidade Alvo</th>
                          <th className="p-2 text-center font-semibold">Reps Fixadas</th>
                          <th className="p-2 text-center font-semibold">Séries</th>
                          <th className="p-2 text-center font-semibold">Intervalo</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sessoes.map((s, idx) => {
                      const dados = intensidades[`${s.date.toISOString()}-${s.treinoIndex}`] || {};
                      return (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-2 pl-4">{s.date.toLocaleDateString()}</td>
                          <td className="p-2 font-semibold text-blue-600">{treinos[s.treinoIndex]?.nome}</td>
                          {fazerPeriodizacao && (
                            <>
                              <td className="p-2 text-center">{dados.intensidade ? `${dados.intensidade}%` : "-"}</td>
                              <td className="p-2 text-center">{dados.reps || "-"}</td>
                              <td className="p-2 text-center">{dados.series || "-"}</td>
                              <td className="p-2 text-center">{dados.intervalo || "-"}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tabelas com as frações de Carga */}
            <div>
              <div className="margin-top-20">Tabelas de Prescrição com Zonas de Intensidade Calculadas</div>

              {fazerPeriodizacao && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "10px 0" }}>
                  <button
                    type="button"
                    className={`btt-popup-1rm ${cargaSource === "descrita" ? "" : "botao-adicionar-exercicio-banco-selecionado"}`}
                    onClick={() => setCargaSource("descrita")}
                  >
                    Usar carga descrita no treino
                  </button>
                  <button
                    type="button"
                    className={`btt-popup-1rm ${cargaSource === "1rm" ? "" : "botao-adicionar-exercicio-banco-selecionado"}`}
                    onClick={selecionarCarga1RM}
                  >
                    Usar carga calculada em 1RM
                  </button>
                </div>
              )}

              {treinos.map((t, ti) => {
                if (!t.exercicios.length) return null;
                return (
                  <div key={ti} className="mb-6 border rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-slate-800 text-white px-4 py-2 font-bold text-sm">{t.nome}</div>
                    <div className="overflow-x-auto">
                      <table className="standard-table w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-100 text-slate-600 font-semibold border-b">
                          <tr>
                            <th className="p-2 pl-4">Exercício</th>
                            <th className="p-2 text-center">Séries</th>
                            <th className="p-2 text-center">Reps Base</th>
                            {fazerPeriodizacao
                              ? percentCols.map((p) => <th key={p} className="p-2 text-center bg-blue-50/30">{p}%</th>)
                              : <th className="p-2 text-center">Intervalo</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {t.exercicios.map((ex) => {
                            const det = t.detalhes?.[ex] || { series: "", reps: "", carga: "", descanso: "", rm1: 0 };
                            const base = cargaSource === "1rm" ? Number(det.rm1 || 0) : Number(det.carga || 0);
                            return (
                              <tr key={ex} className="border-b hover:bg-slate-50 transition-colors">
                                <td className="p-2 pl-4 font-medium text-slate-700">{ex}</td>
                                <td className="p-2 text-center">{det.series || "-"}</td>
                                <td className="p-2 text-center">{det.reps || "-"}</td>
                                {fazerPeriodizacao
                                  ? percentCols.map((p) => {
                                      const val = base * (p / 100);
                                      return <td key={p} className="p-2 text-center font-mono text-slate-600 bg-blue-50/10">{base ? `${formatarNumero(val, 1)}kg` : "-"}</td>;
                                    })
                                  : <td className="p-2 text-center">{det.descanso || "-"}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConteudo = () => {
    if (etapa === 1) return renderEtapaDefinicaoTreinos();
    if (etapa === 2) return renderEtapaCalendario();
    if (etapa === 3) return renderEtapaResultados();
    return null;
  };

  return (
    <div className="avaliacao-container">
      <div className="avaliacao-content flex flex-col min-h-screen justify-between pb-24">
        {renderConteudo()}

        {/* Rodapé Dinâmico das Etapas Consolidadas */}
        <div className="pagination-footer fixed bottom-0 left-0 w-full bg-white border-t border-slate-200 p-4 shadow-lg z-10">
          <div className="sub-pagination-footer max-w-6xl mx-auto flex items-center justify-between gap-4">

            <div className="flex gap-2">
              {etapa > 1 && (
                <button className="next-prev-button flex items-center gap-1 bg-slate-200 text-slate-700 hover:bg-slate-300 p-2 px-4 rounded-md font-bold transition text-sm" type="button" onClick={() => setEtapa(e => e - 1)}>
                  <ArrowLeft size={16} /> Voltar
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {etapa < 3 ? (
                <button className="next-prev-button flex items-center gap-1 bg-blue-600 text-white hover:bg-blue-700 p-2 px-4 rounded-md font-bold transition text-sm shadow-sm" type="button" onClick={() => setEtapa(e => e + 1)}>
                  Avançar <ArrowRight size={16} />
                </button>
              ) : (
                <button className="next-prev-button flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 p-2 px-4 rounded-md font-bold transition text-sm shadow-md" type="button" onClick={handlePrescricaoForcaRegister}>
                  Registrar Ficha <Check size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Modal de Confirmação Final */}
        <Modal isOpen={isNextModal} onRequestClose={closeNextModal} className="modal-next" overlayClassName="popup-overlay">
          <p className="popupNext-h1">Prescrição de Treino de Força finalizada!</p>
          <p className="popupNext-h2">Avaliação concluída! Deseja voltar ao início?</p>
          <div className="sub-pagination-buttons">
            <button className="next-prev-button" type="button" onClick={() => setActiveTab("Anamnese")}>Sim</button>
            <button className="next-prev-button" type="button" onClick={closeNextModal}>Não</button>
          </div>
        </Modal>

        {/* Modal do Teste de 1RM (escopo do treino ativo) */}
        <Modal isOpen={showRMModal} onRequestClose={() => setShowRMModal(false)} className="modal-next modal-teste-1rm" overlayClassName="popup-overlay">
          <h3 className="text-lg font-bold mb-3">Teste de Repetições Máximas</h3>
          <p>Informe a carga utilizada e o número de repetições máximas realizadas para estimar o 1RM de cada exercício.</p>
          <div className="container-tabela-teste-1rm max-h-60 overflow-y-auto">
            <table className="standard-table w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="p-2">Exercício</th>
                  <th className="p-2 text-center w-20">Carga</th>
                  <th className="p-2 text-center w-20">Reps</th>
                  <th className="p-2 text-center">1RM Estimado</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rmTeste).map(([ex, v]) => {
                  const rm = v.carga && v.reps ? (0.033 * v.reps * v.carga) + v.carga : 0;
                  return (
                    <tr key={ex} className="border-b">
                      <td className="p-2 font-medium">{ex}</td>
                      <td className="p-1"><input type="number" className="w-full p-1 border rounded text-center" value={v.carga} onChange={(e) => setRmTeste(p => ({ ...p, [ex]: { ...p[ex], carga: Number(e.target.value) } }))} /></td>
                      <td className="p-1"><input type="number" className="w-full p-1 border rounded text-center" value={v.reps} onChange={(e) => setRmTeste(p => ({ ...p, [ex]: { ...p[ex], reps: Number(e.target.value) } }))} /></td>
                      <td className="p-2 text-center font-bold text-blue-600">{rm ? `${formatarNumero(rm, 1)}kg` : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
            className="w-full mt-4 bg-blue-600 text-white p-2 rounded font-bold hover:bg-blue-700 transition"
            onClick={() => {
              setTreinos((prev) => {
                const next = [...prev];
                Object.entries(rmTeste).forEach(([ex, v]) => {
                  const rm = v.carga && v.reps ? (0.033 * v.reps * v.carga) + v.carga : 0;
                  if (next[treinoAtivoIndex].detalhes[ex]) {
                    next[treinoAtivoIndex].detalhes[ex].rm1 = rm;
                  }
                });
                return next;
              });
              setShowRMModal(false);
            }}
          >
            Aplicar 1RM à Rotina Atual
          </button>
        </Modal>

      </div>
    </div>
  );
}

export default PrescricaoTreinoForca;
